import React, { useEffect, useState } from "react";
import { Placeholder } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import {
  editModuleExercises,
  fetchModuleDetails,
  fetchModuleEvaluations,
  fetchModuleExercises,
} from "./Module.function";
import "./Module.scss";
import ModuleEvaluations from "./components/ModuleEvaluations/ModuleEvaluations";
import ModuleExercises from "./components/ModuleExercises/ModuleExercises";
import ModuleGeneralInformation from "./components/ModuleGeneralInformation/ModuleGeneralInformation";
import ModulePole from "./components/ModulePole/ModulePole";

const Module = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const { module_id } = useParams<{ module_id: string }>();

  const [module, setModule] = useState<any | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [exercises, setExercises] = useState<any[]>([]);
  const [exercisesLoaded, setExercisesLoaded] = useState(false);
  const [activeKey, setActiveKey] = useState<string>("general_info");
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [evaluationsLoaded, setEvaluationsLoaded] = useState(false);

  useEffect(() => {
    if (!module_id) return;
    fetchModuleDetails(module_id, setModule, setLoaded, language);
    fetchModuleExercises(module_id, setExercises, setExercisesLoaded, language);
    fetchModuleEvaluations(module_id, setEvaluations, setEvaluationsLoaded, language);
  }, [module_id, language]);

  const _handleEditExercises = (newExerciseIds: number[], onSuccess?: Function) => {
    editModuleExercises(
      module_id!,
      newExerciseIds,
      () => {
        setExercisesLoaded(false);
        fetchModuleExercises(module_id!, setExercises, setExercisesLoaded, language);
        onSuccess?.();
      },
      language
    );
  };

  if (loaded && !module) {
    return (
      <div className="h-100 d-flex justify-content-center align-items-center flex-column">
        <h2>{translate(language, "PAGES.MODULE.ERROR.NOT_FOUND")}</h2>
        <DstButton
          btnClass="mt-3"
          value={translate(language, "PAGES.MODULE.ERROR.SHOW_ALL_MODULES")}
          clickFunction={() => navigate("/modules")}
        />
      </div>
    );
  }

  const tabsData = [
    {
      id: "general_info",
      title: (
        <React.Fragment>
          <span className="dst-icon-info-circle me-2" /> {translate(language, "PAGES.MODULE.TABS.GENERAL_INFO")}
        </React.Fragment>
      ),
      content: (
        <ModuleGeneralInformation
          moduleData={module}
          language={language}
          onDelete={() => alert("WIP")}
          onEditSave={() => fetchModuleDetails(module_id!, setModule, setLoaded, language)}
        />
      ),
    },
    {
      id: "exercises",
      title: (
        <React.Fragment>
          <span className="dst-icon-list me-2" /> {translate(language, "PAGES.MODULE.TABS.EXERCISES")}
        </React.Fragment>
      ),
      content: (
        <ModuleExercises
          language={language}
          exercises={exercises}
          exercisesLoaded={exercisesLoaded}
          onEditSave={_handleEditExercises}
        />
      ),
    },
    {
      id: "pole",
      title: (
        <React.Fragment>
          <span className="dst-icon-briefcase-02 me-2" /> {translate(language, "PAGES.MODULE.TABS.POLE")}
        </React.Fragment>
      ),
      content: <ModulePole module={module} language={language} />,
    },
    {
      id: "evaluations",
      title: (
        <React.Fragment>
          <span className="dst-icon-check-circle me-2" />
          {translate(language, "PAGES.MODULE.TABS.EVALUATIONS")}
        </React.Fragment>
      ),
      content: (
        <ModuleEvaluations
          moduleId={module_id!}
          language={language}
          evaluations={evaluations}
          evaluationsLoaded={evaluationsLoaded}
          onAddSuccess={() => {
            setEvaluationsLoaded(false);
            fetchModuleEvaluations(module_id!, setEvaluations, setEvaluationsLoaded, language);
          }}
        />
      ),
    },
  ];

  return (
    <div className="container-fluid mt-3 mb-5">
      {module && (
        <Helmet>
          <title>Module - {module.name}</title>
        </Helmet>
      )}

      <div className="d-flex align-items-center mb-3">
        <span
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={(event: any) => completeNavigate(event, navigate, "/modules")}
        />
        <h1 className="mb-0 h3 me-3">
          {module?.name ? (
            module.name
          ) : (
            <Placeholder animation="glow" className="d-flex" style={{ width: 200 }}>
              <Placeholder xs={12} as={"div"} />
            </Placeholder>
          )}
        </h1>
      </div>

      <DstTabs
        tabsData={tabsData}
        variant="primary"
        activeKey={activeKey}
        onSelect={(key) => key && setActiveKey(key)}
        mock={!loaded}
      />
    </div>
  );
};

export default Module;
